import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Tabs, Tab, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { MdDone, MdErrorOutline } from 'react-icons/md';
import style from './AddFoodItemPopup.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addFoodItem, updateFoodItem } from '../../../../store/slices/foodItems/foodItemsThunk';
import { IMAGE_BASE_URL } from '../../../../config';
import { clearFoodItem } from '../../../../store/slices/foodItems/foodItemsSlice';
import { ImageUpload } from '../../../components/image upload/ImageUpload';
// import { updateCategory } from './categorySlice'; // Adjust path
// import { updateCategoryById, addCategory } from './categoryThunk'; // Adjust path

const EMPTY_VALUES = {
  categoryId: '',
  dietary: '',
  basePrice: '',
  translations: {
    en: { name: '', description: '' },
    ka: { name: '', description: '' },
    ru: { name: '', description: '' }
  }
};

export function AddFoodItemPopup({ handleClose, show, fetchFoodItems }) {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.language);
  const [lang, setLang] = useState('en');
  const categoryState = useSelector((state) => state.category);
  const globalMenu = useSelector((state) => state.globalMenu);
  const foodItem = useSelector((state) => state.foodItems);
  const dispatch = useDispatch();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const isEditMode = Boolean(foodItem.currentFoodItem?.id);
  // Inside your component
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageError, setImageError] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file)); // Create a temporary URL for the preview
    }
  };
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: EMPTY_VALUES
  });

  const formValues = watch('translations');

  useEffect(() => {
    if (isEditMode && foodItem.currentFoodItem) {
      const item = foodItem.currentFoodItem;
      reset({
        categoryId: item.foodSectionId,
        basePrice: item.basePrice,
        dietary: item.foodDietary === 'NonVegetarian' ? 'Non Vegetarian' : item.foodDietary,
        translations: item.translationsMap
      });
      // Set existing image preview if available
      if (item.image) {
        setImagePreview(`${IMAGE_BASE_URL}${item.image}`);
      }
    } else {
      reset(EMPTY_VALUES);
      setImagePreview(null);
      setSelectedFile(null);
    }
  }, [isEditMode, foodItem.currentFoodItem, reset]);

  const onSubmit = async (data) => {
    const hasExistingImage = foodItem.currentFoodItem?.image;
    // 1. Validate Image Presence
    if (!selectedFile && !isEditMode && !hasExistingImage) {
      setImageError(t('validation.imageRequired'));
      return;
    }

    // 2. Validate Image Size (2MB)
    const maxSize = 3 * 1024 * 1024;
    if (selectedFile && selectedFile.size > maxSize) {
      setImageError(t('validation.imageTooLarge'));
      return;
    }

    setImageError('');

    const formData = new FormData();

    // 1. Append Top-Level Global Fields
    formData.append('foodSectionId', data.categoryId);
    formData.append('basePrice', parseFloat(data.basePrice));
    formData.append('foodDietary', data.dietary === 'Non Vegetarian' ? 'NonVegetarian' : data.dietary);
    if (isEditMode) {
      const id = foodItem.currentFoodItem.id;
      formData.append('id', id);
    }
    // 2. Append Translations using the indexed format: translations[i][field]
    // We map the translations object keys (en, ka, ru) into an indexed array
    const langKeys = ['en', 'ka'];
    langKeys.forEach((langCode, index) => {
      const translation = data.translations[langCode];

      formData.append(`translations[${index}][languageCode]`, langCode);
      formData.append(`translations[${index}][name]`, translation.name || '');
      formData.append(`translations[${index}][description]`, translation.description || '');
    });

    // 3. Append Image
    if (selectedFile) {
      formData.append('image', selectedFile);
    } else {
    }

    // 4. Dispatch the action
    try {
      if (isEditMode) {
        // We pass the formData which may or may not contain the 'image' key
        await dispatch(updateFoodItem(formData)).unwrap();
      } else {
        await dispatch(addFoodItem(formData)).unwrap();
      }
      handleClosePopup();
      fetchFoodItems(); // Optional: refresh parent list
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  const handleClosePopup = () => {
    setImagePreview(null);
    setSelectedFile(null);
    dispatch(clearFoodItem());
    reset(EMPTY_VALUES);
    setLang('en');
    handleClose();
  };

  const segments = [
    { code: 'en', name: t('common.english') },
    { code: 'ka', name: t('common.georgian') }
    // { code: 'ru', name: t('common.russian') }
  ];
  // Helper to check if a specific language tab has any errors
  const hasTabError = (l) => !!errors.translations?.[l];

  // Helper to check if a language tab is valid and filled
  const isTabComplete = (l) => {
    const trans = watch(`translations.${l}`);
    const basePrice = watch('basePrice');
    const categoryId = watch('categoryId');

    // A tab is complete if its specific name/desc are filled
    // AND the global required fields are filled
    return !!(trans?.name && trans?.description && basePrice && categoryId && !hasTabError(l));
  };

  return (
    <Modal show={show} onHide={handleClosePopup} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="border-0">
        <Row>
          <Col md={12}>
            <Modal.Title className="fw-bold">
              {t(isEditMode ? 'menu_management.food.updateMenuItem' : 'menu_management.food.addMenuItem')}
            </Modal.Title>
          </Col>
          {/* <Col Col md={12}>
          <Tabs activeKey={lang} onSelect={(k) => setLang(k)} className={`mb-4 ${style.customTabs}`}>
          {['en', 'ka', 'ru'].map((l) => (
            <Tab
              key={l}
              eventKey={l}
              title={
                <span className="d-flex align-items-center gap-2">
                  {l === 'en' ? 'English' : l === 'ka' ? 'Georgian' : 'Russian'}
                  {hasTabError(l) ? (
                    <MdErrorOutline size={18} className="text-danger" />
                  ) : (
                    isTabComplete(l) && <MdDone size={18} className="text-success" />
                  )}
                </span>
              }
            />
          ))}
        </Tabs>
          </Col> */}
        </Row>
      </Modal.Header>

      <Modal.Body>
        <div className="w-100 mb-4">
          <div className={style.segmentControl}>
            {segments.map((segment, index) => (
              <button
                key={segment.code}
                type="button"
                onClick={() => {
                  setSelectedTabIndex(index);
                  setLang(segment.code);
                }}
                className={`${style.segmentBtn} ${selectedTabIndex === index ? style.active : ''}`}
              >
                <div className="d-flex align-items-center justify-content-center gap-2">
                  {segment.name}
                  {hasTabError(segment.code) ? (
                    <MdErrorOutline size={18} className="text-danger" />
                  ) : (
                    isTabComplete(segment.code) && <MdDone size={18} className="text-success" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={6}>
              {/* GLOBAL MENU (READ ONLY) */}
              <Form.Group className="mb-4">
                <Form.Label className="small fw-bold text-muted text-uppercase">{t('menu_management.category.selectedMenu')}</Form.Label>
                <Form.Control className="py-2" type="text" value={globalMenu.currentGlobalMenu?.name || ''} disabled />
              </Form.Group>
            </Col>

            {/* CATEGORY SELECT */}
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label className="small fw-bold text-muted text-uppercase">
                  {t('menu_management.food.category')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  className={`py-2 ${errors?.categoryId ? 'is-invalid' : ''}`}
                  {...register(`categoryId`, { required: t('validation.categoryRequired') })}
                >
                  <option value="">{t('menu_management.food.selectCategory')}</option>
                  {categoryState.categories?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.translationsMap?.[currentLanguage]?.name || item.translationsMap?.en?.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors?.categoryId?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {['en', 'ka'].map((l) => (
            <div key={l} className={lang === l ? style.tabFadeIn : 'd-none'}>
              <Row>
                <Col md={12}>
                  {/* NAME */}
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold text-muted text-uppercase">
                      {t('menu_management.food.menuItemName')} ({l.toUpperCase()}) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      className={`py-2 ${errors.translations?.[l]?.name ? 'is-invalid' : ''}`}
                      type="text"
                      placeholder={t('menu_management.category.namePlaceholder')}
                      {...register(`translations.${l}.name`, { required: t('validation.menuItemNameRequired') })}
                    />
                    <Form.Control.Feedback type="invalid">{errors.translations?.[l]?.name?.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {/* DESCRIPTION */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    {t('menu_management.food.menuItemDescription')} ({l.toUpperCase()}) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    className={`py-2 ${errors.translations?.[l]?.description ? 'is-invalid' : ''}`}
                    as="textarea"
                    rows={3}
                    placeholder={t('menu_management.category.descriptionPlaceholder')}
                    {...register(`translations.${l}.description`, { required: t('validation.menuItemDescriptionRequired') })}
                  />
                  <Form.Control.Feedback type="invalid">{errors.translations?.[l]?.description?.message}</Form.Control.Feedback>
                </Form.Group>
              </Row>
            </div>
          ))}

          <Row>
            <Col md={6}>
              {/* Price FIELD */}
              <Form.Group className="mb-4">
                <Form.Label className="small fw-bold text-muted text-uppercase">
                  {t('menu_management.food.menuItemPrice')} <span className="text-danger">*</span>
                </Form.Label>

                <Form.Group as={Form.Group} className="mb-0">
                  <div className="input-group">
                    {/* The static $ sign */}
                    <span className="input-group-text bg-light border-end-0" id={`price-addon`}>
                      ₾
                    </span>

                    <Form.Control
                      className={`py-2 border-start-0 ${errors.basePrice ? 'is-invalid' : ''}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      // aria-describedby={`price-addon-${l}`}
                      {...register(`basePrice`, {
                        required: t('validation.menuItemPriceRequired'),
                        min: { value: 0, message: t('validation.pricePositive') }
                      })}
                    />

                    {/* Individual Error Message */}
                    {errors.basePrice && <Form.Control.Feedback type="invalid">{errors.basePrice.message}</Form.Control.Feedback>}
                  </div>
                </Form.Group>
              </Form.Group>
            </Col>

            {/* DIETARY SELECT (Veg / Non-Veg) */}
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label className="small fw-bold text-muted text-uppercase">
                  {t('menu_management.food.dietary')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  className={`py-2 ${errors?.dietary ? 'is-invalid' : ''}`}
                  {...register(`dietary`, { required: t('validation.dietaryRequired') })}
                >
                  <option value="">{t('menu_management.food.selectDietary')}</option>
                  <option value={t('menu_management.food.veg')}>{t('menu_management.food.veg')}</option>
                  <option value={t('menu_management.food.nonVeg')}>{t('menu_management.food.nonVeg')}</option>
                  {/* <option value="vegan">{t('menu_management.food.vegan')}</option>
                      <option value="vegan">{t('menu_management.food.eggitarian')}</option> */}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors?.dietary?.message}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* IMAGE UPLOAD SECTION */}
            <Form.Group className="mb-4">
              {' '}
              {/* Removed text-center */}
              <Form.Label className="small fw-bold text-muted text-uppercase d-block text-start">
                {t('menu_management.food.itemImage')} <span className="text-danger">*</span>
              </Form.Label>
              <div className="d-flex flex-row gap-3" >
                <ImageUpload
                  imageError={imageError}
                  handleImage={handleImageChange}
                  imagePreview={imagePreview}
                  imagePreviewCancel={() => {
                    setImagePreview(null);
                    setSelectedFile(null);
                  }}
                />
                <div className="text-muted small mt-2">
                  <div>
                    <strong className="fw-bold text-uppercase">{t('common.imageGuidelines.title')}</strong>
                  </div>
                  <ul className="mb-0 ps-3  ">
                    <li>{t('common.imageGuidelines.recommendedSize', { size: '302 px × 302 px' })}</li>
                    <li>{t('common.imageGuidelines.aspectRatio', { ratio: '1:1' })}</li>
                    <li>{t('common.imageGuidelines.format')}</li>
                    <li>{t('common.imageGuidelines.keepCentered')}</li>
                    <li>{t('common.imageGuidelines.avoidBlurry')}</li>
                  </ul>
                </div>
              </div>
            </Form.Group>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="link" className="text-muted text-decoration-none" onClick={handleClosePopup}>
          {t('common.cancel')}
        </Button>
        <Button variant="dark" className="px-4 py-2 shadow-sm" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? t('common.saving') : t(isEditMode ? 'common.update' : 'common.add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
